// Types for the showcase LIFECYCLE surface — extend, deactivate, delete.
//
// 🛑 THIS IS THE FIRST DESTRUCTIVE SURFACE IN THIS CONSOLE. Every other button here creates
// something. These three act on a showcase that may be displaying right now on a real business's
// Apple place card, and NONE of them can be undone — not the delete, and not the deactivation
// either.
//
// ⚠️ DO NOT BLUR DEACTIVATE AND DELETE IN COPY, AND DO NOT REUSE THE LOCATION-ASSET WORDING. Apple
// ships an `undelete` for LOCATION assets; a showcase has neither an undelete nor a re-activate.
// Anything in this surface that implies a showcase can be restored is wrong, and the person reading
// it is about to press a button on a paying client's public presence.

/** The three actions, mirroring the backend's `ShowcaseLifecycleActionEnum`. */
export enum ShowcaseLifecycleActionEnum {
  DEACTIVATE = "DEACTIVATE",
  EXTEND = "EXTEND",
  DELETE = "DELETE",
}

/**
 * Presentation order: DEACTIVATE FIRST. Deactivating stops the showcase displaying; deleting also
 * removes its history. Where both are possible the safe one is offered first, and that ordering is a
 * decision recorded here rather than a layout accident in a JSX file.
 */
export const SHOWCASE_LIFECYCLE_ACTION_ORDER: readonly ShowcaseLifecycleActionEnum[] = [
  ShowcaseLifecycleActionEnum.DEACTIVATE,
  ShowcaseLifecycleActionEnum.EXTEND,
  ShowcaseLifecycleActionEnum.DELETE,
];

export const SHOWCASE_LIFECYCLE_ACTION_LABELS: Record<string, string> = {
  [ShowcaseLifecycleActionEnum.DEACTIVATE]: "Deactivate — stop it displaying",
  [ShowcaseLifecycleActionEnum.EXTEND]: "Extend the end date",
  [ShowcaseLifecycleActionEnum.DELETE]: "Delete — removes it and its history",
};

/** The backend's `ShowcaseLifecycleContractStateEnum`, for rendering a result. */
export enum ShowcaseLifecycleContractStateEnum {
  REFUSED = "REFUSED",
  REJECTED = "REJECTED",
  COMPLETED = "COMPLETED",
  PARTIALLY_COMPLETED = "PARTIALLY_COMPLETED",
  OUTCOME_UNKNOWN = "OUTCOME_UNKNOWN",
}

export const SHOWCASE_LIFECYCLE_STATE_LABELS: Record<string, string> = {
  [ShowcaseLifecycleContractStateEnum.REFUSED]: "Refused — nothing was sent to Apple",
  [ShowcaseLifecycleContractStateEnum.REJECTED]: "Apple refused it — nothing changed",
  [ShowcaseLifecycleContractStateEnum.COMPLETED]: "Done",
  [ShowcaseLifecycleContractStateEnum.PARTIALLY_COMPLETED]: "Partly done — needs a person",
  [ShowcaseLifecycleContractStateEnum.OUTCOME_UNKNOWN]:
    "Outcome unknown — needs a person, do not retry",
};

/** What the CMS writes to enqueue a request, mirroring the onboarding steps' `submitState`. */
export enum LifecycleSubmitStateEnum {
  QUEUED = "QUEUED",
  CLAIMED = "CLAIMED",
  DONE = "DONE",
  FAILED = "FAILED",
}

export const LIFECYCLE_SUBMIT_STATE_LABELS: Record<string, string> = {
  [LifecycleSubmitStateEnum.QUEUED]: "Queued",
  [LifecycleSubmitStateEnum.CLAIMED]: "In progress",
  [LifecycleSubmitStateEnum.DONE]: "Finished",
  [LifecycleSubmitStateEnum.FAILED]: "Failed",
};

/**
 * The subcollection under one `showcase_requests` document. A showcase has ONE create attempt but may
 * have many lifecycle actions over its life, so each action is its own document — and for an action
 * with no undo, one immutable document per attempt is the audit trail.
 */
export const SHOWCASE_LIFECYCLE_REQUESTS_PATH = "lifecycle_requests";
export const SHOWCASE_LIFECYCLE_LABEL = "Showcase lifecycle action";

/**
 * One lifecycle request document.
 *
 * ⚠️ THE THREE `confirmed*` IDS ARE A SNAPSHOT, NOT A SOURCE OF TRUTH. They record what the operator
 * was actually looking at when they confirmed, so that a later reader can tell "we acted on the wrong
 * showcase" from "the record moved afterwards". The backend re-resolves everything from the client's
 * grant and from Apple itself before it acts; it must never trust these.
 */
export type ShowcaseLifecycleRequestEntity = {
  action?: ShowcaseLifecycleActionEnum;
  /** EXTEND only. The new EXCLUSIVE end date, as a UTC-midnight ISO string. */
  requestedEndDate?: string | null;
  /** DELETE only. Defaults to false — see the dialog copy for why that default is deliberate. */
  deleteCreative?: boolean | null;

  confirmedShowcaseId?: string | null;
  confirmedLocationId?: string | null;
  confirmedBrandId?: string | null;
  /** The flight window as the console displayed it at confirmation time. */
  confirmedStartDate?: string | null;
  confirmedEndDate?: string | null;

  submitState?: LifecycleSubmitStateEnum | null;
  requestedAt?: Date | null;
  /** Who pressed the button. For a destructive action, the audit trail needs a name. */
  requestedBy?: string | null;
  /** Set by this console when the enqueue write itself failed. The backend never reads it. */
  writeError?: string | null;

  // ---- Backend-owned, read-only in this console ----
  contractState?: ShowcaseLifecycleContractStateEnum | null;
  humanActionRequired?: boolean | null;
  humanActionMessage?: string | null;
  appleObservedState?: string | null;
  appleObservedLiveness?: string | null;
  creativeDeleted?: boolean | null;
  completedAt?: Date | null;
};

/**
 * Fields this console may write. Everything else on the document belongs to the backend, and
 * `stripLifecycleFieldsTheFormMayNotWrite` drops the rest on save so a stale loaded value can never be
 * echoed back over a fresher backend write. Same discipline as
 * `ONBOARDING_STEP_FORM_OWNED_FIELDS` in the onboarding surface.
 */
export const LIFECYCLE_CMS_OWNED_FIELDS = [
  "action",
  "requestedEndDate",
  "deleteCreative",
  "confirmedShowcaseId",
  "confirmedLocationId",
  "confirmedBrandId",
  "confirmedStartDate",
  "confirmedEndDate",
  "submitState",
  "requestedAt",
  "requestedBy",
  "writeError",
] as const;
